import { useState, useEffect, useMemo } from 'react';
import { Instagram, Music2, Youtube, Twitter, Facebook, Globe, Play, Mail, ShoppingBag, Link as LinkIcon, Calendar, Headphones } from 'lucide-react';
import { loadGoogleFont } from '@/lib/linktree/fonts';
import {
  SiSpotify, SiYoutube, SiTiktok, SiInstagram, SiApplemusic, SiSoundcloud,
  SiFacebook, SiX, SiBandcamp, SiPatreon, SiDiscord,
  SiSnapchat, SiTwitch, SiPinterest, SiThreads, SiWhatsapp, SiTelegram,
  SiTidal, SiYoutubemusic, SiAudiomack, SiMixcloud, SiBeatport, SiPandora, SiNapster,
} from 'react-icons/si';
import { Linkedin as LucideLinkedin } from 'lucide-react';
import type { LinkTreeBlock, LinkTreeTheme } from '@/lib/linktree/types';
import { fontFamily } from '@/lib/linktree/templates';
import { safeUrl, safeCss } from '@/lib/linktree/sanitize';
import { PremiumHero } from './PremiumHero';

interface RendererProps {
  block: LinkTreeBlock;
  theme: LinkTreeTheme;
  onLinkClick?: (block: LinkTreeBlock, url: string) => void;
}

/** Inline brand glyphs for platforms not covered by react-icons/si. */
const DeezerIcon = ({ size = 18, color = 'currentColor', style }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} fill={color} aria-hidden>
    <rect x="15.5" y="4"  width="8.5" height="2.7" rx="0.4"/>
    <rect x="15.5" y="8"  width="8.5" height="2.7" rx="0.4"/>
    <rect x="0"    y="12" width="6.5" height="2.7" rx="0.4"/>
    <rect x="7.5"  y="12" width="6.5" height="2.7" rx="0.4"/>
    <rect x="15.5" y="12" width="8.5" height="2.7" rx="0.4"/>
    <rect x="0"    y="16" width="6.5" height="2.7" rx="0.4"/>
    <rect x="7.5"  y="16" width="6.5" height="2.7" rx="0.4"/>
    <rect x="15.5" y="16" width="8.5" height="2.7" rx="0.4"/>
    <rect x="0"    y="20" width="6.5" height="2.7" rx="0.4"/>
    <rect x="7.5"  y="20" width="6.5" height="2.7" rx="0.4"/>
    <rect x="15.5" y="20" width="8.5" height="2.7" rx="0.4"/>
  </svg>
);
const AmazonMusicIcon = ({ size = 18, color = 'currentColor', style }: any) => (
  <svg width={size} height={size} viewBox="0 0 24 24" style={style} fill={color} aria-hidden>
    <path d="M12 1.5a10.5 10.5 0 1 0 0 21 10.5 10.5 0 0 0 0-21Zm-1.4 5.3 6.4-1.4c.5-.1.9.2.9.7v8.6a2.7 2.7 0 1 1-1.6-2.5V8L11 9.2v7.5a2.7 2.7 0 1 1-1.6-2.5V7.7c0-.4.3-.8.6-.9Z"/>
  </svg>
);

/** Brand platforms available for the Link Button icon picker. */
export const LINK_PLATFORMS: { key: string; label: string; color: string }[] = [
  { key: 'spotify',     label: 'Spotify',      color: '#1DB954' },
  { key: 'applemusic',  label: 'Apple Music',  color: '#FA243C' },
  { key: 'youtubemusic',label: 'YT Music',     color: '#FF0000' },
  { key: 'deezer',      label: 'Deezer',       color: '#A238FF' },
  { key: 'tidal',       label: 'Tidal',        color: '#ffffff' },
  { key: 'amazonmusic', label: 'Amazon Music', color: '#25D1DA' },
  { key: 'soundcloud',  label: 'SoundCloud',   color: '#FF5500' },
  { key: 'bandcamp',    label: 'Bandcamp',     color: '#629AA9' },
  { key: 'beatport',    label: 'Beatport',     color: '#A8E00F' },
  { key: 'audiomack',   label: 'Audiomack',    color: '#FFA200' },
  { key: 'mixcloud',    label: 'Mixcloud',     color: '#52AAD8' },
  { key: 'pandora',     label: 'Pandora',      color: '#3668FF' },
  { key: 'napster',     label: 'Napster',      color: '#3CB4E5' },
  { key: 'youtube',     label: 'YouTube',      color: '#FF0000' },
  { key: 'tiktok',      label: 'TikTok',       color: '#ffffff' },
  { key: 'instagram',   label: 'Instagram',    color: '#E1306C' },
  { key: 'facebook',    label: 'Facebook',     color: '#1877F2' },
  { key: 'x',           label: 'X / Twitter',  color: '#ffffff' },
  { key: 'threads',     label: 'Threads',      color: '#ffffff' },
  { key: 'snapchat',    label: 'Snapchat',     color: '#FFFC00' },
  { key: 'twitch',      label: 'Twitch',       color: '#9146FF' },
  { key: 'discord',     label: 'Discord',      color: '#5865F2' },
  { key: 'pinterest',   label: 'Pinterest',    color: '#E60023' },
  { key: 'linkedin',    label: 'LinkedIn',     color: '#0A66C2' },
  { key: 'whatsapp',    label: 'WhatsApp',     color: '#25D366' },
  { key: 'telegram',    label: 'Telegram',     color: '#26A5E4' },
  { key: 'patreon',     label: 'Patreon',      color: '#FF424D' },
  { key: 'music',       label: 'Music',        color: '#ffffff' },
  { key: 'email',       label: 'Email',        color: '#ffffff' },
  { key: 'shop',        label: 'Shop',         color: '#ffffff' },
  { key: 'tour',        label: 'Tour',         color: '#ffffff' },
  { key: 'website',     label: 'Website',      color: '#ffffff' },
  { key: 'link',        label: 'Link',         color: '#ffffff' },
];

const PLATFORM_ICON_MAP: Record<string, any> = {
  spotify: SiSpotify, youtube: SiYoutube, tiktok: SiTiktok, instagram: SiInstagram,
  applemusic: SiApplemusic, soundcloud: SiSoundcloud, facebook: SiFacebook, x: SiX,
  threads: SiThreads, snapchat: SiSnapchat, twitch: SiTwitch, discord: SiDiscord,
  pinterest: SiPinterest, linkedin: LucideLinkedin, whatsapp: SiWhatsapp, telegram: SiTelegram,
  bandcamp: SiBandcamp, patreon: SiPatreon,
  deezer: DeezerIcon, tidal: SiTidal, amazonmusic: AmazonMusicIcon, youtubemusic: SiYoutubemusic,
  audiomack: SiAudiomack, mixcloud: SiMixcloud, beatport: SiBeatport, pandora: SiPandora, napster: SiNapster,
  music: Headphones, email: Mail, shop: ShoppingBag, tour: Calendar, website: Globe, link: LinkIcon,
};

/** Render a brand/utility icon by platform key. */
export function renderPlatformIcon(key: string, size = 18, color = 'currentColor') {
  const Icon = PLATFORM_ICON_MAP[key];
  if (!Icon) return null;
  return <Icon size={size} color={color} style={{ display: 'block', color }} />;
}

export function platformBrandColor(key: string): string | null {
  return LINK_PLATFORMS.find(p => p.key === key)?.color || null;
}

const SOCIAL_ICONS: Record<string, any> = {
  ...PLATFORM_ICON_MAP,
  twitter: SiX,
  apple: SiApplemusic,
  'apple-music': SiApplemusic,
  apple_music: SiApplemusic,
};

function hexToRgba(hex: string, a = 1): string {
  if (!hex || !hex.startsWith('#')) return `rgba(255,255,255,${a})`;
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(f, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function shadowFor(level: LinkTreeTheme['buttonShadow'], color: string): string {
  switch (level) {
    case 'none':   return 'none';
    case 'soft':   return `0 4px 14px -6px ${hexToRgba(color, 0.35)}`;
    case 'strong': return `0 14px 36px -10px ${hexToRgba(color, 0.55)}`;
    case 'hard':   return `0 6px 0 0 rgba(0,0,0,0.85)`;
    default:       return `0 10px 30px -12px ${hexToRgba(color, 0.5)}`;
  }
}

function relLuminance(hex: string): number {
  if (!hex || !hex.startsWith('#')) return 0.5;
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(f, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Pick a legible text color for a given background hex. */
function autoTextOn(bgHex: string): string {
  return relLuminance(bgHex) > 0.5 ? '#1a1a1a' : '#ffffff';
}

function buttonStyles(theme: LinkTreeTheme): React.CSSProperties {
  const r = theme.buttonRadius;
  const isTransparentBg = theme.buttonColor === 'transparent';
  // Fallback to theme.primary (not hard-coded white) so tour_dates / merch /
  // newsletter Subscribe buttons match the template palette and stay legible
  // on colorful backgrounds (Afrobeat Sunrise, Latin Fuego, etc.).
  const p = (theme.buttonColor && theme.buttonColor !== 'inherit' && theme.buttonColor !== 'transparent')
    ? theme.buttonColor
    : (theme.primary || '#ffffff');
  const s = theme.secondary || theme.accent || p;
  const txt = (theme.buttonTextColor && theme.buttonTextColor !== 'inherit')
    ? theme.buttonTextColor
    : autoTextOn(p);
  const shadow = isTransparentBg ? 'none' : shadowFor(theme.buttonShadow, p);
  const base: React.CSSProperties = {
    borderRadius: r,
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease, opacity 0.18s ease',
  };
  switch (theme.buttonStyle) {
    case 'solid':   return {
      background: isTransparentBg ? 'transparent' : p,
      color: txt,
      boxShadow: shadow,
      borderRadius: r,
      border: isTransparentBg ? 'none' : undefined,
    };
    case 'outline': return {
      ...base,
      background: 'transparent',
      color: txt,
      border: isTransparentBg ? 'none' : `1.5px solid ${hexToRgba(p, 0.85)}`,
      boxShadow: shadow,
    };
    case 'soft':    return {
      ...base,
      background: isTransparentBg ? 'transparent' : `linear-gradient(135deg, ${hexToRgba(p, 0.22)} 0%, ${hexToRgba(s, 0.12)} 100%)`,
      color: txt,
      border: isTransparentBg ? 'none' : `1px solid ${hexToRgba(p, 0.45)}`,
      boxShadow: shadow === 'none' ? 'none' : `${shadow}, inset 0 1px 0 rgba(255,255,255,0.08)`,
    };
    case 'glass':   return {
      ...base,
      background: isTransparentBg ? 'transparent' : 'linear-gradient(135deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)',
      color: txt,
      border: isTransparentBg ? 'none' : '1px solid rgba(255,255,255,0.18)',
      backdropFilter: isTransparentBg ? undefined : 'blur(14px) saturate(140%)',
      WebkitBackdropFilter: isTransparentBg ? undefined : 'blur(14px) saturate(140%)',
      boxShadow: shadow === 'none' ? 'none' : `${shadow}, inset 0 1px 0 rgba(255,255,255,0.12)`,
    };
  }
}

/** Outbound link: anchor when we have a safe URL (so middle-click /
 *  right-click / SEO crawl all work), button otherwise (so disabled state
 *  still renders the same shape). */
function Outbound({ href, onClick, style, children, ariaLabel, disabled }: {
  href?: string | null;
  onClick?: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const safe = !disabled ? safeUrl(href) : null;
  if (!safe) {
    // Either explicitly disabled OR no valid URL yet (e.g. user is still
    // filling out the form). Mark the button as disabled in BOTH the DOM
    // attribute (mouse cursor + browser default) and aria-disabled so
    // screen readers announce the inactive state.
    const inactive = true;
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        aria-disabled={inactive}
        disabled={inactive}
        style={{ ...style, cursor: 'default', border: 0, opacity: disabled ? 0.55 : (style as any)?.opacity ?? 1 }}
      >
        {children}
      </button>
    );
  }
  return (
    <a
      href={safe}
      target="_blank"
      rel="noopener noreferrer nofollow ugc"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{ ...style, textDecoration: 'none', display: style?.display ?? 'inline-block' }}
    >
      {children}
    </a>
  );
}

function placeholderStyle(theme: LinkTreeTheme): React.CSSProperties {
  return {
    height: 130,
    borderRadius: 18,
    background: `linear-gradient(135deg, ${hexToRgba(theme.primary, 0.20)} 0%, rgba(255,255,255,0.03) 100%)`,
    border: `1px dashed ${hexToRgba(theme.primary, 0.5)}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'rgba(255,255,255,0.78)', fontSize: 13, fontWeight: 500, letterSpacing: '0.01em',
    boxShadow: `0 12px 36px -22px ${hexToRgba(theme.primary, 0.55)}`,
  };
}

function getYoutubeId(url: string) {
  const m = url?.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m?.[1];
}

function getYoutubeStartSec(url: string): number {
  if (!url) return 0;
  const t = url.match(/[?&#](?:t|start|time_start)=([^&#]+)/i)?.[1];
  return t ? parseTimeToken(t) : 0;
}

function getVimeoInfo(url: string): { id: string; hash?: string; startSec?: number } | null {
  if (!url) return null;
  const idMatch = url.match(/(?:player\.)?vimeo\.com\/(?:[a-z]+\/(?:[^/?#]+\/)?(?:video\/|videos\/)?)?(\d{5,})/i);
  if (!idMatch) return null;
  const pathHash = url.match(/vimeo\.com\/(?:.*\/)?\d{5,}\/([a-zA-Z0-9]+)/);
  const queryHash = url.match(/[?&]h=([a-zA-Z0-9]+)/);
  // Pull start time from any of: ?t=, &start=, #t=, time_start=  (values like 90, 90s, 1m30s, 01:30)
  const tParam = url.match(/[?&#](?:t|start|time_start)=([^&#]+)/i)?.[1];
  return { id: idMatch[1], hash: pathHash?.[1] || queryHash?.[1], startSec: tParam ? parseTimeToken(tParam) : undefined };
}

function parseTimeToken(raw: string): number {
  if (!raw) return 0;
  const v = decodeURIComponent(raw).trim();
  if (/^\d+$/.test(v)) return parseInt(v, 10);                // "90"
  if (/:/.test(v)) return parseMmSs(v);                       // "1:30" or "01:30:00"
  // "1h2m30s" / "2m30s" / "90s"
  const m = v.match(/(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?/i);
  if (!m) return 0;
  const h = +(m[1] || 0), mm = +(m[2] || 0), ss = +(m[3] || 0);
  return h * 3600 + mm * 60 + ss;
}

function parseMmSs(s: string): number {
  if (!s) return 0;
  const parts = String(s).split(':').map(p => parseInt(p, 10) || 0);
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return 0;
}

function VideoBlock({ d, theme }: { d: any; theme: LinkTreeTheme }) {
  // Autoplay only works when the audio is muted (browser policy).
  const wantsAutoplay = !!d.autoplay;
  const isMuted = d.muted !== false; // default to muted
  const canAutoplay = wantsAutoplay && isMuted;
  const [playing, setPlaying] = useState<boolean>(canAutoplay);
  const [autoplayBlocked, setAutoplayBlocked] = useState<boolean>(false);
  const url: string = d.url || '';
  const ytId = getYoutubeId(url);
  const vimeo = getVimeoInfo(url);
  // Block-level Start time wins; URL-embedded time is used as a fallback.
  const fieldStart = parseMmSs(d.startTime || '0:00');
  const urlStart = vimeo?.startSec || (ytId ? getYoutubeStartSec(url) : 0);
  const start = fieldStart > 0 ? fieldStart : urlStart;

  const wrap: React.CSSProperties = { position: 'relative', paddingTop: '56.25%', borderRadius: 12, overflow: 'hidden', background: '#000' };
  const inner: React.CSSProperties = { position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 };

  // Treat empty / unfinished URLs (e.g. the default "https://vimeo.com/") as a placeholder
  // instead of an error — the user hasn't typed a real link yet.
  const trimmed = url.trim();
  const isUnfinished =
    !trimmed ||
    /^https?:\/\/?$/i.test(trimmed) ||
    /^https?:\/\/(www\.)?(vimeo\.com|youtube\.com|youtu\.be|player\.vimeo\.com)\/?$/i.test(trimmed);
  if (isUnfinished) {
    return <div style={placeholderStyle(theme)}>Add a video URL</div>;
  }

  const isDirectFile = /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
  if (!ytId && !vimeo && !isDirectFile) {
    const looksLikeVimeo = /vimeo\.com/i.test(url);
    return (
      <div style={{ borderRadius: 12, padding: 16, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', color: theme.text, fontSize: 13, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 600, marginBottom: 6, color: '#fca5a5' }}>
          ⚠️ {looksLikeVimeo ? "Couldn't read this Vimeo link" : 'Unsupported video URL'}
        </div>
        {looksLikeVimeo ? (
          <div style={{ opacity: 0.9 }}>
            <div style={{ marginBottom: 6 }}>We couldn't find the video ID in this URL. Open the video on Vimeo and copy the link from the address bar — it should look like one of these:</div>
            <ul style={{ margin: 0, paddingInlineStart: 18, fontFamily: 'monospace', fontSize: 12, opacity: 0.85 }}>
              <li>https://vimeo.com/<b>123456789</b></li>
              <li>https://vimeo.com/123456789?h=<b>abc123def</b> &nbsp;(private link)</li>
              <li>https://vimeo.com/channels/staffpicks/123456789</li>
              <li>https://player.vimeo.com/video/123456789</li>
            </ul>
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.7 }}>
              Tip: links from "Share → Embed code" or shortened links (vimeo.com/m/...) won't work — use the direct page URL.
            </div>
          </div>
        ) : (
          <div style={{ opacity: 0.85 }}>Use a YouTube link (youtube.com / youtu.be), a Vimeo link (vimeo.com), or a direct video file ending in .mp4, .webm, .mov, or .m4v.</div>
        )}
      </div>
    );
  }

  const Poster = ({ bg }: { bg?: string }) => (
    <button
      onClick={() => setPlaying(true)}
      style={{ ...wrap, padding: 0, cursor: 'pointer', border: 0, background: bg ? `#000 url(${bg}) center/cover no-repeat` : '#000' }}
      aria-label="Play video">
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.35) 100%)' }} />
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 64, height: 64, borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.45)' }}>
        <Play size={28} fill="#fff" color="#fff" style={{ marginLeft: 4 }} />
      </div>
    </button>
  );

  const Notice = ({ children }: { children: React.ReactNode }) => (
    <div style={{ marginTop: 8, borderRadius: 10, padding: '8px 12px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', color: theme.text, fontSize: 12, lineHeight: 1.4 }}>
      <span style={{ color: '#fcd34d', fontWeight: 600 }}>⚠ </span>{children}
    </div>
  );

  const unmutedAutoplayNotice = wantsAutoplay && !isMuted ? (
    <Notice>Autoplay is disabled because the audio is not muted. Most browsers block autoplay with sound — enable "Muted" to autoplay, or press play.</Notice>
  ) : null;

  if (ytId) {
    if (!playing) return <>{<Poster bg={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`} />}{unmutedAutoplayNotice}</>;
    const params = new URLSearchParams({
      autoplay: canAutoplay ? '1' : '0',
      mute: isMuted ? '1' : '0',
      controls: '0',
      modestbranding: '1',
      rel: '0',
      playsinline: '1',
      iv_load_policy: '3',
      disablekb: '1',
      fs: '0',
      showinfo: '0',
      ...(start > 0 ? { start: String(start) } : {}),
      ...(d.loop ? { loop: '1', playlist: ytId } : {}),
    });
    return (
      <div style={wrap}>
        <iframe src={`https://www.youtube-nocookie.com/embed/${ytId}?${params}`} title="YouTube video" loading="lazy" style={inner} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen={false} />
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'auto' }} onClick={e => e.preventDefault()} />
      </div>
    );
  }

  if (vimeo) {
    if (!playing) return <>{<Poster />}{unmutedAutoplayNotice}</>;
    const params = new URLSearchParams({
      autoplay: canAutoplay ? '1' : '0',
      muted: isMuted ? '1' : '0',
      controls: '0',
      title: '0',
      byline: '0',
      portrait: '0',
      ...(vimeo.hash ? { h: vimeo.hash } : {}),
      ...(d.loop ? { loop: '1' } : {}),
    });
    const hash = start > 0 ? `#t=${start}s` : '';
    return (
      <div style={wrap}>
        <iframe src={`https://player.vimeo.com/video/${vimeo.id}?${params}${hash}`} title="Vimeo video" loading="lazy" style={inner} allow="autoplay; encrypted-media; picture-in-picture" />
        <div style={{ position: 'absolute', inset: 0 }} onClick={e => e.preventDefault()} />
      </div>
    );
  }

  // Direct video file
  if (!playing) return <>{<Poster />}{unmutedAutoplayNotice}</>;
  const safeVideoUrl = safeUrl(url);
  if (!safeVideoUrl) return <div style={placeholderStyle(theme)}>Unsupported video URL</div>;
  return (
    <div>
      <div style={wrap}>
        <video
          src={safeVideoUrl}
          muted={isMuted}
          autoPlay={canAutoplay}
          loop={!!d.loop}
          playsInline
          controls={false}
          preload="metadata"
          onLoadedMetadata={e => {
            const v = e.currentTarget as HTMLVideoElement;
            if (start > 0 && start < (v.duration || Infinity)) v.currentTime = start;
            if (canAutoplay) {
              const p = v.play();
              if (p && typeof p.catch === 'function') {
                p.catch(() => setAutoplayBlocked(true));
              }
            }
          }}
          onPlay={() => setAutoplayBlocked(false)}
          style={{ ...inner, objectFit: 'cover' }}
        />
        {autoplayBlocked && (
          <button
            onClick={e => {
              const v = (e.currentTarget.parentElement?.querySelector('video') as HTMLVideoElement | null);
              v?.play().then(() => setAutoplayBlocked(false)).catch(() => {});
            }}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.55)', border: 0, color: '#fff', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 16, textAlign: 'center' }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: theme.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Play size={24} fill="#fff" color="#fff" style={{ marginLeft: 3 }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Autoplay blocked by your browser</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Tap to play</div>
          </button>
        )}
      </div>
      {unmutedAutoplayNotice}
    </div>
  );
}

export function BlockRenderer({ block, theme: baseTheme, onLinkClick }: RendererProps) {
  const d = block.data || {};
  const s = (d.style || {}) as {
    bgColor?: string;
    textColor?: string;
    font?: 'sans'|'serif'|'mono'|'display';
    fontFamily?: string;
    fontWeight?: number;
    fontItalic?: boolean;
  };
  const theme: LinkTreeTheme = {
    ...baseTheme,
    ...(s.font ? { font: s.font } : {}),
    ...(s.bgColor ? { buttonColor: s.bgColor } : {}),
    ...(s.textColor ? { text: s.textColor, buttonTextColor: s.textColor } : {}),
  };

  // Map theme.font preset → Google Font family so weights/italic always load.
  const themeFontFamily =
    theme.font === 'serif' ? 'Playfair Display' :
    theme.font === 'mono' ? 'JetBrains Mono' :
    'Plus Jakarta Sans';

  // Load custom Google Font OR base theme font with the chosen weight/italic on demand.
  useEffect(() => {
    const family = s.fontFamily || themeFontFamily;
    const weights = Array.from(new Set([400, s.fontWeight ?? 400]));
    loadGoogleFont(family, weights, !!s.fontItalic);
  }, [s.fontFamily, s.fontWeight, s.fontItalic, themeFontFamily]);

  const baseFont = fontFamily(theme.font);
  const font = s.fontFamily ? `'${s.fontFamily}', ${baseFont}` : baseFont;
  const fw = s.fontWeight;
  const fst = s.fontItalic ? 'italic' as const : undefined;
  const cardBg = s.bgColor ?? theme.cardBg; // for card-style blocks (header/bio/text/music_player/newsletter)
  const cardBorder = theme.cardBorder ?? 'rgba(255,255,255,0.1)';


  switch (block.type) {
    case 'header': return (
      <div style={{ textAlign: 'center', padding: cardBg ? '24px 16px' : '24px 0', fontFamily: font, fontWeight: fw, fontStyle: fst, background: cardBg, borderRadius: cardBg ? 16 : undefined }}>
        {d.avatar && safeUrl(d.avatar) && <img src={safeUrl(d.avatar)!} alt="" loading="lazy" decoding="async" width={96} height={96} style={{ width: 96, height: 96, borderRadius: '50%', margin: '0 auto 16px', objectFit: 'cover', border: `2px solid ${theme.primary}` }} />}
        {!d.avatar && <div style={{ width: 96, height: 96, borderRadius: '50%', margin: '0 auto 16px', background: `linear-gradient(135deg,${theme.primary},${theme.secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, color: '#fff', fontWeight: 700 }}>{(d.name || 'A').charAt(0)}</div>}
        <h2 style={{ fontSize: 26, fontWeight: fw ?? 700, color: theme.text, margin: 0, fontStyle: fst }}>{d.name || 'Your Name'}</h2>
        {d.tagline && <p style={{ fontSize: 14, color: theme.text, opacity: 0.7, marginTop: 6, fontWeight: fw, fontStyle: fst }}>{d.tagline}</p>}
      </div>
    );

    case 'bio': return (
      <p style={{ textAlign: 'center', color: theme.text, opacity: 0.85, fontSize: 14, lineHeight: 1.6, padding: cardBg ? '16px 18px' : '0 8px', fontFamily: font, fontWeight: fw, fontStyle: fst, margin: 0, background: cardBg, borderRadius: cardBg ? 14 : undefined }}>{d.text}</p>
    );

    case 'link': {
      const hasStyle = !!theme.buttonStyle;
      const bs = hasStyle
        ? buttonStyles(theme)
        : {
            background: '#ffffff',
            color: '#0a0a0a',
            border: '1px solid rgba(0,0,0,0.06)',
            transition: 'transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, border-color 0.18s ease, opacity 0.18s ease',
          } as React.CSSProperties;
      const radius = theme.buttonRadius != null ? theme.buttonRadius : 9999;
      const baseShadow = hasStyle ? (bs as any).boxShadow : shadowFor(theme.buttonShadow, theme.buttonColor || theme.primary || '#000');
      const thumb = d.thumbnail || d.image;
      return (
        <Outbound
          href={d.url}
          onClick={() => d.url && onLinkClick?.(block, d.url)}
          ariaLabel={d.label || 'Open link'}
          style={{
            ...bs,
            width: '100%',
            height: 48,
            minHeight: 48,
            maxHeight: 48,
            padding: '0 20px',
            fontSize: 14,
            fontWeight: fw ?? 500,
            fontStyle: fst,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            border: 'none',
            outline: 'none',
            borderRadius: radius,
            boxShadow: baseShadow,
            position: 'relative',
            fontFamily: font,
            boxSizing: 'border-box',
          }}>
          {thumb && safeUrl(thumb) && (
            <img
              src={safeUrl(thumb)!}
              alt=""
              loading="lazy" decoding="async" width={36} height={36}
              style={{
                position: 'absolute',
                left: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 36,
                height: 36,
                borderRadius: Math.min(radius, 18),
                objectFit: 'cover',
                boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              }}
            />
          )}
          {!thumb && d.iconPlatform && (() => {
            const brand = platformBrandColor(d.iconPlatform);
            const iconColor = d.iconMono ? 'currentColor' : (brand && brand !== '#ffffff' ? brand : 'currentColor');
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 0, color: iconColor }}>
                {renderPlatformIcon(d.iconPlatform, 18, iconColor)}
              </span>
            );
          })()}
          {!thumb && !d.iconPlatform && d.icon && <span style={{ fontSize: 15, lineHeight: 1 }}>{d.icon}</span>}
          <span style={{ flex: thumb ? '1 1 auto' : undefined }}>{(() => {
            const PLATFORM_LABELS: Record<string, string> = {
              spotify: 'Spotify', youtube: 'YouTube', soundcloud: 'SoundCloud',
              deezer: 'Deezer', applemusic: 'Apple Music', bandcamp: 'Bandcamp',
              tidal: 'Tidal', amazonmusic: 'Amazon Music', youtubemusic: 'YT Music',
              beatport: 'Beatport', audiomack: 'Audiomack', mixcloud: 'Mixcloud',
              pandora: 'Pandora', napster: 'Napster',
            };
            if (d.iconPlatform && PLATFORM_LABELS[d.iconPlatform]) return PLATFORM_LABELS[d.iconPlatform];
            return d.label || 'Click here';
          })()}</span>
        </Outbound>
      );
    }

    case 'social_row': return (
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
        {(d.items || []).filter((i: any) => i.url).map((it: any, i: number) => {
          const key = (it.platform || '').toLowerCase().replace(/[\s-]/g, '_');
          const Icon = SOCIAL_ICONS[key] || SOCIAL_ICONS[key.replace('_', '')] || Globe;
          const brand = platformBrandColor(key) || platformBrandColor(key.replace('_', ''));
          const iconColor = d.iconMono ? '#fff' : (brand && brand !== '#ffffff' ? brand : '#fff');
          return (
            <Outbound key={i}
              href={it.url}
              onClick={() => onLinkClick?.(block, it.url)}
              ariaLabel={it.platform}
              style={{
                width: 42, height: 42, borderRadius: '50%',
                background: `linear-gradient(135deg, ${hexToRgba(theme.primary, 0.22)} 0%, rgba(255,255,255,0.04) 100%)`,
                border: `1px solid ${hexToRgba(theme.primary, 0.5)}`,
                color: iconColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 22px -14px ${hexToRgba(theme.primary, 0.55)}`,
                transition: 'transform 0.18s ease, background 0.18s ease, border-color 0.18s ease',
              }}>
              <Icon size={18} color={iconColor} style={{ color: iconColor }} />
            </Outbound>
          );
        })}
      </div>
    );

    case 'handle': {
      const platKey = (d.platform || 'instagram').toLowerCase();
      const brand = platformBrandColor(platKey);
      const iconColor = d.iconMono ? '#fff' : (brand && brand !== '#ffffff' ? brand : '#fff');
      const handleText = d.handle || '@username';
      const clickable = !!d.url;
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Outbound
            href={d.url}
            disabled={!clickable}
            onClick={() => clickable && onLinkClick?.(block, d.url)}
            ariaLabel={`${platKey} ${handleText}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 16px', borderRadius: 9999,
              background: 'rgba(20,20,24,0.85)',
              border: '1px solid rgba(255,255,255,0.10)',
              color: theme.text, fontFamily: font, fontWeight: fw ?? 500, fontStyle: fst,
              fontSize: 13,
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              boxShadow: '0 4px 14px -6px rgba(0,0,0,0.5)',
              transition: 'transform 0.18s ease, background 0.18s ease',
            }}
          >
            <span style={{ display: 'inline-flex', lineHeight: 0, color: iconColor }}>
              {renderPlatformIcon(platKey, 14, iconColor) || <Globe size={14} />}
            </span>
            <span style={{ opacity: 0.95 }}>{handleText}</span>
          </Outbound>
        </div>
      );
    }

    case 'spotify_embed': {
      const url = d.url || '';
      const m = url.match(/spotify\.com\/(track|album|playlist|artist)\/([a-zA-Z0-9]+)/);
      if (!m) return <div style={placeholderStyle(theme)}>Add a Spotify URL</div>;
      return <iframe src={`https://open.spotify.com/embed/${m[1]}/${m[2]}`} title="Spotify player" loading="lazy" width="100%" height="152" frameBorder="0" allow="encrypted-media" style={{ borderRadius: 16, display: 'block', boxShadow: `0 14px 40px -18px ${hexToRgba(theme.primary, 0.55)}` }} />;
    }
    case 'youtube_embed': {
      const id = getYoutubeId(d.url || '');
      if (!id) return <div style={placeholderStyle(theme)}>Add a YouTube URL</div>;
      const fieldStart = parseTimeToken(String(d.startTime ?? ''));
      const urlStart = getYoutubeStartSec(d.url || '');
      const startSec = fieldStart > 0 ? fieldStart : urlStart;
      const params: string[] = [];
      if (startSec > 0) params.push(`start=${startSec}`);
      if (d.autoplay) { params.push('autoplay=1', 'mute=1', 'playsinline=1'); }
      else if (d.muted) params.push('mute=1');
      if (d.hideChrome) params.push('controls=0', 'modestbranding=1', 'rel=0', 'showinfo=0', 'iv_load_policy=3', 'disablekb=1', 'fs=0', 'cc_load_policy=0', 'color=white');
      const qs = params.length ? `?${params.join('&')}` : '';
      const maskColor = (theme as any).cardBg || theme.bg || '#0a0a0a';
      return <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 16, overflow: 'hidden', boxShadow: `0 16px 44px -20px ${hexToRgba(theme.primary, 0.55)}`, border: '1px solid rgba(255,255,255,0.08)' }}>
        <iframe key={qs} src={`https://www.youtube-nocookie.com/embed/${id}${qs}`} title="YouTube video" loading="lazy" allow="autoplay; encrypted-media; picture-in-picture" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }} allowFullScreen />
        {d.hideChrome && (<>
          <div aria-hidden style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 56, background: maskColor, pointerEvents: 'none' }} />
          <div aria-hidden style={{ position: 'absolute', bottom: 0, right: 0, width: 110, height: 44, background: maskColor, pointerEvents: 'none' }} />
        </>)}
      </div>;
    }
    case 'soundcloud_embed': {
      const scSafe = safeUrl(d.url);
      if (!scSafe) return <div style={placeholderStyle(theme)}>Add a SoundCloud URL</div>;
      return <iframe width="100%" height="120" frameBorder="0" title="SoundCloud player" loading="lazy" src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(scSafe)}&color=%23${theme.primary.replace('#','')}&inverse=true&auto_play=false`} style={{ borderRadius: 16, display: 'block', boxShadow: `0 14px 40px -18px ${hexToRgba(theme.primary, 0.55)}` }} />;
    }
    case 'apple_music_embed': {
      const url = d.url || '';
      // Convert any music.apple.com URL → embed.music.apple.com
      const embedUrl = url.replace(/^https?:\/\/(?:geo\.)?music\.apple\.com/i, 'https://embed.music.apple.com');
      if (!/embed\.music\.apple\.com/i.test(embedUrl)) return <div style={placeholderStyle(theme)}>Add an Apple Music URL</div>;
      return <iframe src={embedUrl} title="Apple Music player" loading="lazy" allow="autoplay *; encrypted-media *;" sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation" width="100%" height="175" frameBorder="0" style={{ borderRadius: 16, display: 'block', background: 'transparent', boxShadow: `0 14px 40px -18px ${hexToRgba(theme.primary, 0.55)}` }} />;
    }
    case 'cover_hero': {
      if (theme.hideHero) return null;
      const cover = safeUrl(d.image || d.cover);
      const overlay = safeCss(theme.heroOverlay, 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.55) 100%)');
      const tint = safeCss(theme.heroTint, `linear-gradient(135deg, ${hexToRgba(theme.primary, 0.18)} 0%, transparent 45%, ${hexToRgba(theme.secondary || theme.accent || theme.primary, 0.22)} 100%)`);
      const title = d.title || '';
      const tagline = d.tagline || d.subtitle || '';
      return (
        <div style={{ position: 'relative', width: '100%', borderRadius: 16, overflow: 'hidden', minHeight: 180, background: cover ? `url("${cover}") center/cover no-repeat` : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary || theme.accent || theme.primary})` }}>
          {tint && tint !== 'transparent' && <div style={{ position: 'absolute', inset: 0, background: tint, mixBlendMode: 'overlay' }} />}
          <div style={{ position: 'absolute', inset: 0, background: overlay }} />
          {(title || tagline) && (
            <div style={{ position: 'relative', padding: '24px 20px', minHeight: 180, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', color: '#fff', fontFamily: font, fontWeight: fw, fontStyle: fst }}>
              {title && <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>{title}</div>}
              {tagline && <div style={{ marginTop: 6, fontSize: 14, opacity: 0.9, textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>{tagline}</div>}
            </div>
          )}
        </div>
      );
    }

    case 'music_player': return (
      <div style={{
        background: cardBg || `linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.025) 100%)`,
        border: `1px solid ${cardBorder}`,
        borderRadius: 18, padding: 16, display: 'flex', alignItems: 'center', gap: 14, fontFamily: font, fontWeight: fw, fontStyle: fst,
        boxShadow: `0 16px 44px -22px ${hexToRgba(theme.primary, 0.55)}, inset 0 1px 0 rgba(255,255,255,0.08)`,
        backdropFilter: 'blur(14px)',
      }}>
        <div style={{
          width: 52, height: 52, borderRadius: 10,
          background: safeUrl(d.coverUrl) ? `url("${safeUrl(d.coverUrl)}") center/cover` : `linear-gradient(135deg,${theme.primary},${theme.secondary || theme.primary})`,
          flexShrink: 0,
          boxShadow: `0 8px 22px -10px ${hexToRgba(theme.primary, 0.6)}`,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || 'Track title'}</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{d.artist || ''}</div>
        </div>
        {d.audioUrl && safeUrl(d.audioUrl) && <audio controls src={safeUrl(d.audioUrl)!} style={{ height: 32, maxWidth: 160 }} />}
      </div>
    );

    case 'tour_dates': return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {(d.dates || []).filter((x: any) => x.city || x.venue).map((dt: any, i: number) => (
          <Outbound key={i}
            href={dt.url}
            disabled={!dt.url}
            onClick={() => dt.url && onLinkClick?.(block, dt.url)}
            ariaLabel={`${dt.city || ''} ${dt.venue || ''}`.trim() || 'Tour date'}
            style={{ ...buttonStyles(theme), width: '100%', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontFamily: font, fontWeight: fw, fontStyle: fst, boxSizing: 'border-box' }}>
            <div style={{ textAlign: 'left', minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
              <div style={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dt.city}</div>
              <div style={{ fontSize: 12, opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dt.venue}</div>
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, whiteSpace: 'nowrap', flexShrink: 0 }}>{dt.date}</div>
          </Outbound>
        ))}
      </div>
    );


    case 'merch': return (
      <Outbound
        href={d.url}
        onClick={() => d.url && onLinkClick?.(block, d.url)}
        ariaLabel={d.title ? `Buy ${d.title}` : 'Buy merch'}
        style={{ ...buttonStyles(theme), width: '100%', padding: 16, display: 'flex', gap: 12, alignItems: 'center', textAlign: 'left', fontFamily: font, fontWeight: fw, fontStyle: fst, boxSizing: 'border-box' }}>
        <div style={{ width: 56, height: 56, borderRadius: 8, background: safeUrl(d.image) ? `url("${safeUrl(d.image)}") center/cover` : `${theme.accent}33`, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{d.title}</div>
          <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>{d.price}</div>
        </div>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Buy →</span>
      </Outbound>
    );

    // Newsletter is temporarily disabled (Coming Soon). We render nothing so
    // any legacy blocks that survived in the DB stay hidden in public pages.
    case 'newsletter': return null;

    case 'image': {
      const imgUrl = safeUrl(d.url);
      return imgUrl
        ? <img src={imgUrl} alt={d.alt || ''} loading="lazy" decoding="async" style={{ width: '100%', borderRadius: 12, display: 'block' }} />
        : <div style={placeholderStyle(theme)}>Add an image URL</div>;
    }

    case 'video': return <VideoBlock d={d} theme={theme} />;

    case 'text': {
      const sizes: Record<string, number> = { sm: 13, md: 15, lg: 20, xl: 28 };
      const fs = sizes[d.size || 'md'] || 15;
      return <p style={{ color: theme.text, fontSize: fs, fontFamily: font, fontWeight: fw, fontStyle: fst, textAlign: 'center', margin: 0, lineHeight: 1.5, background: cardBg, padding: cardBg ? '14px 16px' : undefined, borderRadius: cardBg ? 14 : undefined }}>{d.text}</p>;
    }

    case 'divider': return <hr style={{ border: 0, borderTop: `1px solid ${theme.dividerColor ?? `${theme.text}22`}`, margin: 0 }} />;
    case 'spacer': return <div style={{ height: d.size || 24 }} />;

    default: return null;
  }
}

interface PreviewProps {
  blocks: LinkTreeBlock[];
  theme: LinkTreeTheme;
  onLinkClick?: (block: LinkTreeBlock, url: string) => void;
  selectedId?: string | null;
  onSelectBlock?: (id: string) => void;
  transparent?: boolean;
  /** When provided, renders the same premium hero as the published page. */
  page?: { title: string | null; bio: string | null; avatar_url: string | null; cover_url: string | null };
}

export function LinkTreePreview({ blocks, theme, onLinkClick, selectedId, onSelectBlock, transparent, page }: PreviewProps) {
  const visible = blocks.filter(b => b.is_visible !== false);
  const showHero = !!page && !theme.hideHero;
  const headerBlock = showHero ? visible.find(b => b.type === 'header') : undefined;
  // Only auto-consume the header block in the hero. The social_row block
  // should render in its own position so users can reorder it freely; the
  // hero socials only appear when there is no social_row block at all.
  const socialBlock = showHero ? visible.find(b => b.type === 'social_row') : undefined;
  const hasSocialBlock = !!socialBlock;
  const consumed = new Set<string>();
  if (headerBlock) consumed.add(headerBlock.id);
  const restBlocks = visible.filter(b => !consumed.has(b.id));

  const accent = theme.secondary || theme.accent || theme.primary;
  // Honor theme.bg (Custom colors > Page > Background) when set; otherwise use cover/ambient fallback.
  const fallbackAmbient = page?.cover_url
    ? `linear-gradient(180deg, rgba(8,8,14,0.78) 0%, rgba(8,8,14,0.94) 60%, #07070b 100%), url(${page.cover_url}) center/cover no-repeat`
    : `radial-gradient(1100px 700px at 50% -10%, rgba(245,158,11,0.45), transparent 60%),
       radial-gradient(900px 600px at 100% 30%, rgba(251,191,36,0.30), transparent 60%),
       radial-gradient(900px 600px at 0% 90%, rgba(245,158,11,0.22), transparent 60%),
       linear-gradient(160deg, #7c2d12 0%, #4c1d95 50%, #0c0a1a 100%)`;
  const ambient = theme.bg || fallbackAmbient;

  if (page) {
    const headerData = headerBlock?.data || { name: page.title, tagline: page.bio, avatar: page.avatar_url };
    const socials = hasSocialBlock ? [] : ((socialBlock?.data?.items as any[]) || []).filter((it: any) => it && it.url && String(it.url).trim());
    return (
      <div style={{ background: transparent ? 'transparent' : ambient, color: theme.text, minHeight: '100%', padding: 0, fontFamily: fontFamily(theme.font) }}>
        <div style={{
          width: '100%', margin: 0, padding: 0, borderRadius: 0, overflow: 'hidden',
          background: 'transparent',
        }}>
          <div style={{
            background: 'transparent',
            borderRadius: 0, overflow: 'hidden',
          }}>
            {showHero && (
              <div
                onClick={onSelectBlock && headerBlock ? () => onSelectBlock(headerBlock.id) : undefined}
                style={{
                  cursor: onSelectBlock && headerBlock ? 'pointer' : 'default',
                  outline: selectedId && headerBlock && selectedId === headerBlock.id ? `2px solid ${theme.primary}` : 'none',
                  outlineOffset: -2,
                }}>
                <PremiumHero page={page} headerData={headerData} socials={socials} theme={theme} />
              </div>
            )}

            <div style={{ padding: '20px 0 32px', background: 'transparent' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {restBlocks.map(b => (
                  <div key={b.id}
                    onClick={onSelectBlock ? () => onSelectBlock(b.id) : undefined}
                    style={{
                      cursor: onSelectBlock ? 'pointer' : 'default',
                      outline: selectedId === b.id ? `2px solid ${theme.primary}` : 'none',
                      outlineOffset: 4, borderRadius: 12,
                    }}>
                    <BlockRenderer block={b} theme={theme} onLinkClick={onLinkClick} />
                  </div>
                ))}
                {restBlocks.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 40, opacity: 0.5, fontSize: 14 }}>
                    Add blocks from the left to start building your page.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: transparent ? 'transparent' : theme.bg, color: theme.text, minHeight: '100%', padding: '24px 0 60px', fontFamily: fontFamily(theme.font) }}>
      <div style={{ width: '100%', margin: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {visible.map(b => (
          <div key={b.id}
            onClick={onSelectBlock ? () => onSelectBlock(b.id) : undefined}
            style={{
              cursor: onSelectBlock ? 'pointer' : 'default',
              outline: selectedId === b.id ? `2px solid ${theme.primary}` : 'none',
              outlineOffset: 4,
              borderRadius: 12,
            }}>
            <BlockRenderer block={b} theme={theme} onLinkClick={onLinkClick} />
          </div>
        ))}
        {blocks.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, opacity: 0.5, fontSize: 14 }}>
            Add blocks from the left to start building your page.
          </div>
        )}
      </div>
    </div>
  );
}
